/**
 * @author Theodore Kruczek
 * @description Orbital Object ToolKit (ootk) is a collection of tools for working
 * with satellites and other orbital objects.
 * @license AGPL-3.0-or-later
 * @copyright (c) 2025 Kruczek Labs LLC
 *
 * Many of the classes are based off of the work of @david-rc-dayton and his
 * Pious Squid library (https://github.com/david-rc-dayton/pious_squid) which
 * is licensed under the MIT license.
 *
 * Orbital Object ToolKit is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * Orbital Object ToolKit is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * Orbital Object ToolKit. If not, see <http://www.gnu.org/licenses/>.
 */
import { Earth } from '../body/Earth';
import { J2000 } from './J2000';
import { StateVector } from './StateVector';
/**
 * True Equator Mean Equinox (TEME) is a coordinate system commonly used in satellite tracking and orbit prediction. It
 * is a reference frame that defines the position and orientation of an object relative to the Earth's equator and
 * equinox.
 *
 * By using the True Equator Mean Equinox (TEME) coordinate system, we can accurately describe the position and motion
 * of satellites relative to the Earth's equator and equinox. This is particularly useful for tracking and predicting
 * satellite orbits in various applications, such as satellite communication, navigation, and remote sensing.
 */
export class TEME extends StateVector {
    /**
     * Gets the name of the coordinate system.
     * @returns The name of the coordinate system.
     */
    get name() {
        return 'TEME';
    }
    /**
     * Gets a value indicating whether the coordinate is inertial.
     * @returns A boolean value indicating whether the coordinate is inertial.
     */
    get inertial() {
        return true;
    }
    /**
     * Creates a TEME (True Equator Mean Equinox) object from classical orbital elements.
     * @param elements - The classical orbital elements.
     * @returns A new TEME object.
     */
    static fromClassicalElements(elements) {
        const rv = elements.toPositionVelocity();
        return new TEME(elements.epoch, rv.position, rv.velocity);
    }
    /**
     * Converts the TEME (True Equator Mean Equinox) coordinates to J2000 coordinates.
     * @returns The J2000 coordinates.
     */
    toJ2000() {
        const p = Earth.precession(this.epoch);
        const n = Earth.nutation(this.epoch);
        const eps = n.mEps + n.dEps;
        const dPsiCosEps = n.dPsi * Math.cos(eps);
        const rMOD = this.position
            .rotZ(-dPsiCosEps)
            .rotX(eps)
            .rotZ(n.dPsi)
            .rotX(-n.mEps);
        const vMOD = this.velocity
            .rotZ(-dPsiCosEps)
            .rotX(eps)
            .rotZ(n.dPsi)
            .rotX(-n.mEps);
        const rJ2K = rMOD
            .rotZ(p.zed)
            .rotY(-p.theta)
            .rotZ(p.zeta);
        const vJ2K = vMOD
            .rotZ(p.zed)
            .rotY(-p.theta)
            .rotZ(p.zeta);
        return new J2000(this.epoch, rJ2K, vJ2K);
    }
}
